import {FetchPolicy} from '@apollo/client';
import {objectFromEntries} from '@benchling/js-utils';
import gql from 'graphql-tag';

import {assert} from 'Asserts';
import {pluralize} from 'BenchlingUtil';
import {ValidationStateInfo} from 'constants/SampleConstants';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {accessibleObjs} from 'graphql-client/InaccessibleInfo';
import {InventoryValidationErrorLabel} from 'inventory/InventoryConstants';
import {
  ParentStorableCapacityQuery,
  ParentStorableCapacityQueryVariables,
} from 'inventory/location-validation/__generated/ParentStorableCapacityQuery';
import {ParentStorableMinimalCapacityQuery} from 'inventory/location-validation/__generated/ParentStorableMinimalCapacityQuery';
import {
  StorablesSearchListingValidationStatusQuery,
  StorablesSearchListingValidationStatusQueryVariables,
} from 'inventory/location-validation/__generated/StorablesSearchListingValidationStatusQuery';
import {getAvailableCapacity} from 'samples/SampleUtil';
import {ListableItem} from 'search/SearchConstants';
import APIUtil from 'util/APIUtil';
import {capitalize} from 'util/NameUtil';
import {MakeFieldsOptional} from 'util/Types';

type LocationWithMinimalCapacityInfo = ExtractByTypename<
  ParentStorableMinimalCapacityQuery['storables'][number],
  'LocationNode'
>;

type StorableOrInaccessibleWithCapacityInfo = ParentStorableCapacityQuery['storables'][number];

type LocationWithCapacityInfo = ExtractByTypename<StorableOrInaccessibleWithCapacityInfo, 'LocationNode'>;

type GridWithCapacityInfo =
  | ExtractByTypename<StorableOrInaccessibleWithCapacityInfo, 'BoxNode'>
  | ExtractByTypename<StorableOrInaccessibleWithCapacityInfo, 'PlateNode'>;

export type ParentStorableWithCapacityInfo =
  | GridWithCapacityInfo
  | MakeFieldsOptional<
      LocationWithCapacityInfo,
      'allowsNonLocationChildren' | 'allowedChildStorableSchemaApiIds'
    >;

// If the items are being created, they won't have an id. All we really need here are the schemas, but
// this shape allows the caller to get the ids of the invalid children if they want to display them.
export interface PotentialChildStorable {
  id?: string;
  storableSchema?: {
    id: string;
    name: string;
  };
}

interface CapacityValidityStatus {
  isValid: boolean;
  errorMessage: string | null;
  invalidChildren?: Array<PotentialChildStorable>;
}

export interface CapacityValidityErrorInfo {
  isValid: boolean;
  isOvercapacity: boolean;
  hasDisallowedChildren?: boolean;
  hasInvalidSchemaChildren?: boolean;
  errorDetails: {
    availableCapacity: number | null;
    desiredCapacityUsage: number;
    invalidChildren?: Array<PotentialChildStorable>;
    allowedChildStorableSchemaApiIds?: Array<string> | null;
  };
}

export function isValidParentStorableWithCapacityInfo(
  parent: StorableOrInaccessibleWithCapacityInfo | LocationWithMinimalCapacityInfo
): parent is ParentStorableWithCapacityInfo {
  return ['PlateNode', 'BoxNode', 'LocationNode'].includes(parent.__typename);
}

export const PARENT_STORABLE_CAPACITY_QUERY = gql`
  query ParentStorableCapacityQuery($storableIds: [StrictString!]!)
  @skipAudit
  @engComponent(name: INVENTORY) {
    storables(ids: $storableIds) {
      ... on StorableInterface {
        id
        name
      }
      ... on PlateNode {
        id
        totalCapacity
        occupiedCapacity
      }
      ... on BoxNode {
        id
        totalCapacity
        occupiedCapacity
      }
      ... on LocationNode {
        id
        totalCapacity: capacity
        occupiedCapacity
        allowsNonLocationChildren
        allowedChildStorableSchemaApiIds
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

/**
 * Occupied/total capacity only query.
 *
 * This query intentionally excludes the additional location configuration fields in favor of only the total
 * & occupied capacity. This is needed for a cache-only fetch of this data for cross-row notebook table
 * validation, which cannot currently be async. The total & occupied capacity information is queried for &
 * entered into the cache from the chip queries, and can be read here. The other location fields are not present
 * in the chip popover and their absence would cause cache reads of the full capacity query to fail.
 */
export const PARENT_STORABLE_MINIMAL_CAPACITY_QUERY = gql`
  query ParentStorableMinimalCapacityQuery($storableIds: [StrictString!]!)
  @skipAudit
  @engComponent(name: INVENTORY) {
    storables(ids: $storableIds) {
      ... on StorableInterface {
        id
        name
      }
      ... on PlateNode {
        id
        totalCapacity
        occupiedCapacity
      }
      ... on BoxNode {
        id
        totalCapacity
        occupiedCapacity
      }
      ... on LocationNode {
        id
        totalCapacity: capacity
        occupiedCapacity
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

/**
 * Queries for parent's capacity configuration, and validates moving items into it based on this config.
 *
 * Validates items being moved in based on capacity, allowing non-location children, and allowable child
 * schema types.
 * Returns whether the move is valid, a possible error message, and items in violation of restrictions on
 * allowable child schemas, such as if non-location children are disallowed or allowable child schema types
 * are specified.
 *
 * Frontend counterpart of `validate_items_for_parent_storable` in benchling/inventory/storables/validate.py,
 * to be kept in sync.
 */
export async function validateItemsForParentStorable(
  parentStorableId: string,
  potentialChildren: Array<PotentialChildStorable>,
  fetchPolicy?: FetchPolicy
): Promise<CapacityValidityStatus> {
  const {storables} = await doQuerySafe<ParentStorableCapacityQuery, ParentStorableCapacityQueryVariables>(
    {query: PARENT_STORABLE_CAPACITY_QUERY},
    {variables: {storableIds: [parentStorableId]}, fetchPolicy}
  );
  const [storable] = storables;
  if (!isValidParentStorableWithCapacityInfo(storable)) {
    // Null error message because invalid parent (e.g. container) will be handled elsewhere, and we don't
    // want to override that error message with one here
    return {isValid: false, errorMessage: null};
  }
  const capacityErrorInfo = areItemsValidForParentStorable(storable, potentialChildren);
  const errorMessage = getCapacityErrorMessage(storable.name, capacityErrorInfo);
  return {
    isValid: capacityErrorInfo.isValid,
    errorMessage,
    invalidChildren: capacityErrorInfo.errorDetails.invalidChildren,
  };
}

/**
 * Return the main error message based on validity error info dict, which could have multiple errors.
 *
 * Prioritizes location not allowing children, and then invalid schemas. Capacity is last because it does
 * not matter if children are not allowed at all or if the items are of the wrong schema.
 */
export function getCapacityErrorMessage(
  parentName: string,
  errorInfo: CapacityValidityErrorInfo,
  useShortMessages?: boolean
): string | null {
  if (errorInfo.isValid) {
    return null;
  }
  if (errorInfo.hasDisallowedChildren) {
    const baseMsg = 'not configured to allow plates, boxes, or containers';
    return useShortMessages ? baseMsg : `${parentName} is ${baseMsg}.`;
  }
  if (errorInfo.hasInvalidSchemaChildren) {
    const invalidChildren = errorInfo.errorDetails.invalidChildren ?? [];
    assert(
      invalidChildren.every(({storableSchema}) => storableSchema != null),
      'Expected all invalid schema children to have defined schemas'
    );
    const invalidSchemaNames = [
      ...new Set(invalidChildren.map(({storableSchema}) => storableSchema!.name) ?? []),
    ];
    const baseSchemaMsg = `not configured to store items`;
    const fullSchemaMsg =
      invalidSchemaNames.length === 1
        ? `${baseSchemaMsg} of the ${invalidSchemaNames[0]} schema`
        : `${baseSchemaMsg} of the following schemas: ${invalidSchemaNames.join(', ')}`;
    return useShortMessages ? fullSchemaMsg : `${parentName} is ${fullSchemaMsg}.`;
  }
  if (errorInfo.isOvercapacity) {
    const availableCapacity = errorInfo.errorDetails.availableCapacity!;
    if (availableCapacity === 0) {
      return useShortMessages ? 'no available space' : `${parentName} has no available space.`;
    }
    if (useShortMessages) {
      return `not enough open positions (${errorInfo.errorDetails.desiredCapacityUsage} needed, but only ${availableCapacity} available)`;
    }
    return `${parentName} has space for ${availableCapacity} additional ${pluralize(
      errorInfo.errorDetails.availableCapacity!,
      'item'
    )}, but ${errorInfo.errorDetails.desiredCapacityUsage} items are being moved in.`;
  }
  assert(false, 'Expected a validity error with isValid=False', {errorInfo});
}

/**
 * Validate whether items can be moved into/created in the parent storable.
 *
 * Considers capacity for grids & locations, and whether location allows non-location children and which
 * schemas it allows. Returns an error info dict with which condition was violated.
 *
 * Frontend counterpart of `_are_items_valid_for_parent_storable_helper` function in
 * benchling/inventory/storables/validate.py, to be kept in sync. This function allows checking for and
 * rendering those errors exclusively on the FE.
 */
export function areItemsValidForParentStorable(
  parentStorable: ParentStorableWithCapacityInfo,
  potentialChildren: Array<PotentialChildStorable>
): CapacityValidityErrorInfo {
  const nonLocationChildren = potentialChildren.filter(
    ({storableSchema}) => storableSchema == null || !APIUtil.isLocationSchema(storableSchema.id)
  );
  const availableCapacity =
    parentStorable.occupiedCapacity != null && parentStorable.totalCapacity != null
      ? getAvailableCapacity(parentStorable.totalCapacity, parentStorable.occupiedCapacity)
      : null;
  const isOvercapacity =
    (availableCapacity != null && availableCapacity < nonLocationChildren.length) || false;
  if (parentStorable.__typename !== 'LocationNode') {
    return {
      isValid: !isOvercapacity,
      isOvercapacity,
      errorDetails: {
        availableCapacity,
        desiredCapacityUsage: nonLocationChildren.length,
      },
    };
  }

  const hasDisallowedChildren =
    parentStorable.allowsNonLocationChildren != null &&
    !parentStorable.allowsNonLocationChildren &&
    nonLocationChildren.length > 0;
  const childrenWithInvalidSchemas =
    parentStorable.allowedChildStorableSchemaApiIds != null
      ? nonLocationChildren.filter(
          ({storableSchema}) =>
            storableSchema != null &&
            !parentStorable.allowedChildStorableSchemaApiIds!.includes(storableSchema.id)
        )
      : [];
  // Items of the wrong schemas should not count towards overcapacity errors
  const validCapacityUsage = nonLocationChildren.length - childrenWithInvalidSchemas.length;
  const locationIsOvercapacity =
    availableCapacity != null ? availableCapacity < validCapacityUsage : isOvercapacity;
  return {
    isValid: !locationIsOvercapacity && !hasDisallowedChildren && childrenWithInvalidSchemas.length === 0,
    isOvercapacity: locationIsOvercapacity,
    hasDisallowedChildren,
    hasInvalidSchemaChildren: childrenWithInvalidSchemas.length > 0,
    errorDetails: {
      availableCapacity,
      desiredCapacityUsage: validCapacityUsage,
      invalidChildren: hasDisallowedChildren ? nonLocationChildren : childrenWithInvalidSchemas,
      allowedChildStorableSchemaApiIds: parentStorable.allowedChildStorableSchemaApiIds,
    },
  };
}

/**
 * Returns display text for a validation error label.
 *
 * @param errorLabel Validation error label to get display text for
 * @param includeContainsPrefix Whether to prefix display text with "Contains". For example, set to true for
 * search filters, where we're searching for locations that have invalid items, and set to false for location
 * schema validation page where we're looking at counts of the actual invalid items.
 */
export function inventoryValidationErrorLabelToDisplayText(
  errorLabel: InventoryValidationErrorLabel,
  includeContainsPrefix: boolean = true
): string {
  const errorLabelToBaseText = {
    [InventoryValidationErrorLabel.OVER_CAPACITY]: 'Over capacity',
    [InventoryValidationErrorLabel.ITEM_TYPES_DISALLOWED]: 'items of invalid schemas',
    [InventoryValidationErrorLabel.NON_LOCATION_ITEMS_DISALLOWED]: 'invalid non-location items',
  };
  if (errorLabel === InventoryValidationErrorLabel.OVER_CAPACITY) {
    return errorLabelToBaseText[errorLabel];
  }
  return includeContainsPrefix
    ? `Contains ${errorLabelToBaseText[errorLabel]}`
    : capitalize(errorLabelToBaseText[errorLabel]);
}

export const STORABLES_SEARCH_LISTING_VALIDATION_STATUS_QUERY = gql`
  query StorablesSearchListingValidationStatusQuery($storableIds: [StrictString!]!)
  @skipAudit
  @engComponent(name: INVENTORY) {
    storables(ids: $storableIds) {
      ... on StorableInterface {
        id
        validationState {
          id
          status
          validationErrorRecords {
            id
            errorLabel
            message
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export interface StorableValidationInfoById {
  [storableId: string]: ValidationStateInfo;
}

interface StorableValidationQueryResponse {
  type: QueryResultType;
  storableValidationInfoById: StorableValidationInfoById;
  refetch: () => void;
}

// Undefined is returned when the query is skipped - meaning none of the items are storables. This is done
// intentionally to prevent extra rerenders on the ExpandedItemsTable if there are no meaningful state changes
// See https://github.com/benchling/aurelia/pull/2728 for more details
export function useStorableValidationInfoQuery(
  items: Array<ListableItem>
): StorableValidationQueryResponse | undefined {
  const storableIds = items.flatMap((item) => (APIUtil.isStorable(item.id) ? [item.id] : []));
  const result = useSkippableBenchlingQuery<
    StorablesSearchListingValidationStatusQuery,
    StorablesSearchListingValidationStatusQueryVariables
  >(STORABLES_SEARCH_LISTING_VALIDATION_STATUS_QUERY, {
    skip: storableIds.length === 0,
    variables: {storableIds},
    fetchPolicy: 'cache-and-network',
    renderCachedDataWhenRefetching: true,
  });

  const baseResponse = {type: result.type, refetch: result.refetch};
  if (result.type === QueryResultType.SKIPPED) {
    return undefined;
  } else if (result.type === QueryResultType.PENDING || result.type === QueryResultType.ERROR) {
    return {...baseResponse, storableValidationInfoById: {}};
  } else {
    return {
      ...baseResponse,
      storableValidationInfoById: objectFromEntries(
        accessibleObjs(result.data.storables).map((item) => [item.id, item])
      ),
    };
  }
}
